
import React from 'react';
import { useFormState } from '@/hooks/useFormState';
import FormLayout from './FormLayout';
import PersonalInfo from './FormSteps/PersonalInfo';
import ProtectedCharacteristicsStep from './FormSteps/ProtectedCharacteristics';
import SupportingDocuments from './FormSteps/SupportingDocuments';
import DiscriminationTypeStep from './FormSteps/DiscriminationType';
import Review from './FormSteps/Review';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import DeepgramKeyInput from './DeepgramKeyInput';

const DiscriminationForm: React.FC = () => {
  const {
    formData,
    currentStep,
    isSubmitting,
    isCompleted,
    progressPercentage,
    deepgramApiKey,
    updateFormData,
    goToNextStep,
    goToPrevStep,
    resetForm,
    submitForm,
    validateCurrentStep,
    setApiKey,
    addFile,
    removeFile
  } = useFormState();

  // Add DeepgramKeyInput component at the top level to set the API key automatically
  // This will run once when the form loads and won't be visible to users
  React.useEffect(() => {
    // Set the permanent API key
    setApiKey("8ec5e65274afa8b4aaddbacdac4f1d98b95f6f90");
  }, [setApiKey]);

  if (isCompleted) {
    return (
      <FormLayout progressPercentage={100}>
        <div className="text-center space-y-6 py-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center animate-fade-in">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">Form Submitted Successfully</h2>
            <p className="text-muted-foreground">
              Thank you for sharing your experience. Your information has been submitted.
            </p>
          </div>
          <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 justify-center pt-4">
            <button
              onClick={resetForm}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 h-9 px-4 py-2"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Submit Another Response
            </button>
          </div>
        </div>
      </FormLayout>
    );
  }

  const isValid = validateCurrentStep();

  return (
    <FormLayout progressPercentage={progressPercentage}>
      {/* Include DeepgramKeyInput component here but it won't render any UI */}
      <DeepgramKeyInput onApiKeySet={setApiKey} apiKey={deepgramApiKey} />
      
      {currentStep === 'personal' && (
        <PersonalInfo
          formData={formData}
          updateFormData={updateFormData}
          onNext={goToNextStep}
          isValid={isValid}
        />
      )}

      {currentStep === 'protectedCharacteristics' && (
        <ProtectedCharacteristicsStep
          formData={formData}
          updateFormData={updateFormData}
          onNext={goToNextStep}
          onPrev={goToPrevStep}
          isValid={isValid}
        />
      )}
      
      {currentStep === 'discriminationType' && (
        <DiscriminationTypeStep
          formData={formData}
          updateFormData={updateFormData}
          onNext={goToNextStep}
          onPrev={goToPrevStep}
          isValid={isValid}
          deepgramApiKey={deepgramApiKey}
          setApiKey={setApiKey}
          addFile={addFile}
          removeFile={removeFile}
        />
      )}
      
      {currentStep === 'supportingDocuments' && (
        <SupportingDocuments
          uploadedFiles={formData.uploadedFiles}
          onNext={goToNextStep}
          onPrev={goToPrevStep}
          addFile={addFile}
          removeFile={removeFile}
        />
      )}

      {currentStep === 'review' && (
        <Review
          formData={formData}
          onPrev={goToPrevStep}
          onSubmit={submitForm}
          isSubmitting={isSubmitting}
        />
      )}
    </FormLayout>
  );
};

export default DiscriminationForm;
